import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API call helper with error handling
export const apiCall = async (method, endpoint, data = null, config = {}) => {
  try {
    const response = await apiClient({
      method,
      url: endpoint,
      data,
      ...config,
    });
    return response;
  } catch (error) {
    console.error(`API Error [${method.toUpperCase()} ${endpoint}]:`, error);
    throw error;
  }
};

// Story API functions
export const storyApi = {
  getAll: () => apiCall('get', '/stories'),
  getById: (id) => apiCall('get', `/stories/${id}`),
  getFeatured: () => apiCall('get', '/stories/featured'),
  getTrending: () => apiCall('get', '/stories/trending'),
  getByGenre: (genreId) => apiCall('get', `/stories?genre_id=${genreId}`),
  create: (data) => apiCall('post', '/stories', data),
  update: (id, data) => apiCall('put', `/stories/${id}`, data),
  delete: (id) => apiCall('delete', `/stories/${id}`),
};

// Chapter API functions
export const chapterApi = {
  getByStory: (storyId) => apiCall('get', `/stories/${storyId}/chapters`),
  getById: (chapterId) => apiCall('get', `/chapters/${chapterId}`),
  create: (storyId, data) => apiCall('post', `/stories/${storyId}/chapters`, data),
  update: (chapterId, data) => apiCall('put', `/chapters/${chapterId}`, data),
  unlock: (chapterId) => apiCall('post', `/chapters/${chapterId}/unlock`),
};

// User API functions
export const userApi = {
  getProfile: () => apiCall('get', '/profile'),
  updateProfile: (data) => apiCall('put', '/profile', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiCall('post', '/profile/upload-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Library API functions
export const libraryApi = {
  get: () => apiCall('get', '/library'),
  add: (storyId) => apiCall('post', `/library/${storyId}`),
  remove: (storyId) => apiCall('delete', `/library/${storyId}`),
  check: (storyId) => apiCall('get', `/library/check/${storyId}`),
};

// Wallet API functions
export const walletApi = {
  getBalance: () => apiCall('get', '/coins/balance'),
  getTransactions: () => apiCall('get', '/coins/transactions'),
  createCheckoutSession: (priceId, packageName) => 
    apiCall('post', '/coins/create-checkout-session', { price_id: priceId, package_name: packageName }),
  verifySession: (sessionId) => apiCall('post', '/coins/verify-session', { session_id: sessionId }),
};

// Comments API functions
export const commentsApi = {
  getByStory: (storyId) => apiCall('get', `/comments/${storyId}`),
  create: (data) => apiCall('post', '/comments', data),
  flag: (commentId, reason) => apiCall('post', `/comments/${commentId}/flag`, { reason }),
};

// Votes API functions
export const votesApi = {
  getByStory: (storyId) => apiCall('get', `/votes/${storyId}`),
  toggle: (storyId) => apiCall('post', `/votes/${storyId}`),
};

// Author API functions
export const authorApi = {
  getPublicProfile: (authorId) => apiCall('get', `/authors/${authorId}/public`),
  getStories: (authorId) => apiCall('get', `/authors/${authorId}/stories`),
  getStats: () => apiCall('get', '/author/stats'),
  getEarnings: () => apiCall('get', '/author/earnings'),
  applyContract: () => apiCall('post', '/author/contract/apply'),
};

// Genre & Theme API functions
export const contentApi = {
  getGenres: () => apiCall('get', '/genres'),
  getThemes: () => apiCall('get', '/themes'),
};

// Contest API functions
export const contestApi = {
  getLeaderboard: (type) => apiCall('get', `/contest/leaderboard/${type}`),
  getSettings: (contestId) => apiCall('get', `/contest/settings/${contestId}`),
};

// Referral API functions
export const referralApi = {
  generate: () => apiCall('post', '/referral/generate'),
  getStats: () => apiCall('get', '/referral/stats'),
  track: (code) => apiCall('post', '/referral/track', { code }),
};

// Admin API functions
export const adminApi = {
  getPendingStories: () => apiCall('get', '/admin/stories/pending'),
  approveStory: (storyId) => apiCall('post', `/admin/stories/${storyId}/approve`),
  rejectStory: (storyId, reason) => apiCall('post', `/admin/stories/${storyId}/reject`, { reason }),
  getEarnings: () => apiCall('get', '/admin/earnings'),
  getEarningsByAuthor: () => apiCall('get', '/admin/earnings/by-author'),
  approveEarning: (id) => apiCall('post', `/admin/earnings/${id}/approve`),
  markPaid: (id) => apiCall('post', `/admin/earnings/${id}/mark-paid`),
  getUsers: () => apiCall('get', '/admin/users'),
  getFlaggedComments: () => apiCall('get', '/admin/comments/flagged'),
  deleteComment: (id) => apiCall('delete', `/admin/comments/${id}`),
  dismissFlag: (id) => apiCall('post', `/admin/comments/${id}/dismiss-flag`),
};

export default apiClient;
